import { GenericLazyString } from '@eventbrite/i18n';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { handleAcceptToS, handleShowToS } from '../../../actions/tos';
import { CAPTCHA_FIELD } from '../../../constants';
import {
    TOSMODAL_NEWACCOUNT_ACCEPTED,
    TOSMODAL_NEWACCOUNT_SHOWED,
    TOSMODAL_SOCIAL_DENIED,
} from '../../../constants/analytics';
import {
    IAllContextsProps,
    IFormikActions,
    ISignupActionData,
    ISignupFormProps,
} from '../../../constants/interfaces';
import {
    FORM_FIELD_INVALID_EMAIL,
    FORM_FIELD_REQUIRED,
} from '../../../constants/loginMessages';
import {
    FORM_FIELD_EMAIL_MISMATCHES,
    FORM_FIELD_LAST_NAME_LENGTH,
    FORM_FIELD_LAST_NAME_REQUIRED,
    FORM_FIELD_LAST_NAME_SPACES,
    FORM_FIELD_NAME_LENGTH,
    FORM_FIELD_NAME_REQUIRED,
    FORM_FIELD_NAME_SPACES,
    FORM_FIELD_PASSWORD_LENGTH,
} from '../../../constants/signupMessages';
import { TOS_SOCIAL_MUST_BE_ACCEPTED } from '../../../constants/tosMessages';
import {
    IConfigContextProps,
    useConfigContext,
} from '../../../contexts/ConfigContext';
import {
    IFrontendContext,
    MODAL_TYPES,
    useFrontendContext,
} from '../../../contexts/FrontendContext';
import {
    INotificationContext,
    useNotificationsContext,
} from '../../../contexts/NotificationsContext';
import { IUserContext, useUserContext } from '../../../contexts/UserContext';
import { trackEditEmailMismatch } from '../../../tracking/signup';
import CaptchaField from '../../common/CaptchaField';
import EmailField from '../../common/EmailField';
import ConfirmEmailField from '../../common/EmailField/ConfirmEmailField';
import FormSubmitButton from '../../common/FormSubmitButton';
import SignupFields from '../../common/SignupFields';
import StaticEmailField from '../../common/StaticEmailField';
import PrivacyTerms from '../../PrivacyTerms';
import ToSDialog from '../../ToSDialog';
import VerificationDialog from '../../VerificationDialog';

const rejectInitialSpaces = (value?: string) => !value || !!value?.trim();

const SignupSchema = Yup.object().shape({
    email: Yup.string()
        .email(FORM_FIELD_INVALID_EMAIL.toString())
        .required(FORM_FIELD_REQUIRED.toString()),
    emailConfirmation: Yup.string()
        .required(FORM_FIELD_EMAIL_MISMATCHES.toString())
        .oneOf(
            [Yup.ref('email'), null],
            FORM_FIELD_EMAIL_MISMATCHES.toString(),
        ),

    firstName: Yup.string()
        .max(50, FORM_FIELD_NAME_LENGTH.toString())
        .test('', FORM_FIELD_NAME_SPACES.toString(), (value) =>
            rejectInitialSpaces(value),
        )
        .required(FORM_FIELD_NAME_REQUIRED.toString()),
    lastName: Yup.string()
        .max(50, FORM_FIELD_LAST_NAME_LENGTH.toString())
        .test('', FORM_FIELD_LAST_NAME_SPACES.toString(), (value) =>
            rejectInitialSpaces(value),
        )
        .required(FORM_FIELD_LAST_NAME_REQUIRED.toString()),
    password: Yup.string()
        .min(8, FORM_FIELD_PASSWORD_LENGTH.toString())
        .required(FORM_FIELD_REQUIRED.toString()),
});

const SignupForm = ({
    email,
    routing,
    handleSubmit,
    submitButtonText,

    onEmailEdit,
    onShowPasswordClick,
    onChangePassword,
}: {
    email: string | undefined;
    routing?: any;
    handleSubmit: (props: {
        formData: ISignupActionData;
        contexts: IAllContextsProps;
        formikActions: IFormikActions;
    }) => void;
    submitButtonText: GenericLazyString;

    // Email Field handlers
    onEmailEdit?: () => void;

    // Password Field handlers
    onShowPasswordClick?: ({
        contexts,
    }: {
        contexts: IAllContextsProps;
    }) => void;
    onChangePassword?: () => void;
}) => {
    const frontendContext: IFrontendContext = useFrontendContext();
    const notificationsContext: INotificationContext =
        useNotificationsContext();
    const configContext: IConfigContextProps = useConfigContext();
    const userContext: IUserContext = useUserContext();
    const contexts = {
        frontendContext,
        userContext,
        configContext,
        notificationsContext,
    };

    const initialValues: ISignupFormProps = {
        email: email ?? '',
        emailConfirmation: '',
        firstName: '',
        lastName: '',
        password: '',
    };

    const buttonClasses =
        'eds-fx--fade-in-down eds-fx--delay-1 eds-l-mar-top-8';

    const twoStepsSignup = configContext.social?.SSOSettings?.twoStepsSignup;

    const [enableCaptchaV3, setEnableCaptchaV3] =
        React.useState<boolean>(false);
    const [captchaV3Token, setCaptchaV3Token] = React.useState<string>('');
    const [captchaV2Token, setCaptchaV2Token] = React.useState<string>('');
    const [captchaV2Required, setCaptchaV2Required] =
        React.useState<boolean>(false);

    React.useEffect(() => {
        setCaptchaV2Required(!captchaV2Token);
    }, [captchaV2Token]);

    const handleCaptchaV3 = (captchaToken: string) => {
        setEnableCaptchaV3(false);
        setCaptchaV3Token(captchaToken);
    };

    const handleCaptchaV2 = (captchaToken: string, setFieldValue: any) => {
        setCaptchaV2Token(captchaToken);

        setFieldValue(CAPTCHA_FIELD, captchaToken);
    };

    const handlePasswordChange = () => {
        setEnableCaptchaV3(true);

        onChangePassword?.();
    };

    const _handleSubmit = async (
        formValues: ISignupFormProps,
        formikActions: IFormikActions,
    ) => {
        const hydratedData: ISignupActionData = {
            ...formValues,
            captchaV2Token,
            captchaV3Token,
        };

        await handleSubmit({
            formData: hydratedData,
            contexts: {
                frontendContext,
                notificationsContext,
                configContext,
                userContext,
            },
            formikActions,
        });

        setCaptchaV2Token('');

        formikActions.setSubmitting(false);
    };

    const handleToSInterceptor = () => {
        handleShowToS({
            action: TOSMODAL_NEWACCOUNT_SHOWED,
            routing,
            contexts,
        });
    };

    const handleVerificationInterceptor = () => {
        frontendContext.methods.setShowModal(MODAL_TYPES.verification);
    };

    const conditionalHandlerInterceptor = () => {
        if (configContext?.featureFlags?.launchSignupWithVerificationCode) {
            handleVerificationInterceptor();
        } else {
            handleToSInterceptor();
        }
    };

    const _handleOnNewUserClick = (
        formValues: ISignupFormProps,
        formikActions: IFormikActions,
    ) => {
        handleAcceptToS({
            action: TOSMODAL_NEWACCOUNT_ACCEPTED,
            formData: formValues,
            onAccept: () => {
                _handleSubmit(formValues, formikActions);
            },
            contexts,
        });
    };

    const _handleVerificationCodeSuccessful = () => {
        contexts.frontendContext?.methods.setShowToSDialog(true);
        contexts.frontendContext?.methods.setShowModal(MODAL_TYPES.closed);
    };

    let EmailFieldComponent: any = EmailField;

    if (email) {
        EmailFieldComponent = StaticEmailField;
    }

    return (
        <>
            <Formik
                initialValues={initialValues}
                validationSchema={SignupSchema}
                onSubmit={conditionalHandlerInterceptor}
            >
                {(formikActions: any & IFormikActions) => {
                    const { values, setFieldValue, errors, touched } =
                        formikActions;
                    return (
                        <>
                            <Form
                                data-testid="form-login"
                                method="post"
                                noValidate
                            >
                                <div>
                                    <Field
                                        name="email"
                                        component={EmailFieldComponent}
                                        value={values.email}
                                        isStatic={!!values.email}
                                        bottomSpacing={3}
                                        onEmailEdit={() => {
                                            userContext.methods.lookup.setLookedAccount(
                                                '',
                                            );
                                            onEmailEdit?.();
                                        }}
                                        onEmailMismatch={() => {
                                            trackEditEmailMismatch({
                                                analytics:
                                                    configContext.analytics,
                                            });
                                        }}
                                        errors={errors}
                                        shouldRevealEmail={true}
                                    />

                                    <Field
                                        name="emailConfirmation"
                                        component={ConfirmEmailField}
                                        value={values.emailConfirmation}
                                        isStatic={false}
                                        bottomSpacing={3}
                                        autoFocus={!!values.email}
                                        email={values.email}
                                        errors={errors}
                                        touched={touched}
                                    />

                                    <SignupFields
                                        values={values}
                                        errors={errors}
                                        touched={touched}
                                        onChangePassword={handlePasswordChange}
                                        onShowPasswordClick={
                                            onShowPasswordClick
                                        }
                                    />
                                </div>

                                {configContext?.captcha && (
                                    <CaptchaField
                                        showCaptcha={
                                            frontendContext?.state?.showCaptcha
                                        }
                                        captcha={configContext.captcha}
                                        captchaV2Required={captchaV2Required}
                                        handleCaptchaV2={(value: string) => {
                                            handleCaptchaV2(
                                                value,
                                                setFieldValue,
                                            );
                                        }}
                                        handleCaptchaV3={handleCaptchaV3}
                                        enableCaptchaV3={enableCaptchaV3}
                                        setEnableCaptchaV3={setEnableCaptchaV3}
                                    />
                                )}

                                <div className="eds-l-pad-bot-10">
                                    <FormSubmitButton
                                        submitDisabled={
                                            frontendContext?.state.signupOngoing
                                        }
                                        isLoading={
                                            frontendContext?.state.signupOngoing
                                        }
                                        dataTestId="signup-submit-button"
                                        buttonCTA={submitButtonText}
                                        __containerClassName={buttonClasses}
                                    />
                                </div>
                            </Form>

                            <VerificationDialog
                                email={values.email}
                                target="sign_up"
                                onVerificationSuccessful={() => {
                                    _handleVerificationCodeSuccessful();
                                }}
                            />

                            <ToSDialog
                                dataTestId="signup-tos"
                                cancelActionName={TOSMODAL_SOCIAL_DENIED}
                                cancelNotificationMessage={
                                    TOS_SOCIAL_MUST_BE_ACCEPTED
                                }
                                routing={routing}
                                handleAcceptToS={() => {
                                    _handleOnNewUserClick(
                                        values,
                                        formikActions,
                                    );
                                }}
                            />
                        </>
                    );
                }}
            </Formik>

            {twoStepsSignup && <PrivacyTerms />}
        </>
    );
};

export default SignupForm;
